// components/common/PostModal.js
import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import Stories from 'react-insta-stories';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faEdit,
  faTrash,
  faFlag,
  faShare,
  faChevronLeft,
  faChevronRight,
  faEllipsisV,
  faLink,
  faEye,
  faEyeSlash,
  faMusic,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import UserContext from '../../context/UserContext';
import { usePostContext } from '../../context/PostContext';
import { useNavigate, useLocation } from 'react-router-dom';
import defaultProfilePhoto from '../../assets/Images/defaultUserProfilePicture.png';
import styles from './PostModal.module.css';

// Cache for author data to prevent re-fetching
const authorDataCache = new Map();

// Media cache implementation
const mediaCache = {
  cache: new Map(),
  maxSize: 200 * 1024 * 1024, // 100MB cache size
  currentSize: 0,
  
  async getItem(url) {
    if (!url) return null;
    
    try {
      // Check if the URL is already in the cache
      if (this.cache.has(url)) {
        const cacheEntry = this.cache.get(url);
        cacheEntry.lastAccessed = Date.now();
        return cacheEntry.objectUrl;
      }
      return null;
    } catch (error) {
      console.error('Error accessing media cache:', error);
      return null;
    }
  },
  
  async setItem(url, blob) {
    if (!url || !blob) return null;
    
    try {
      // Create object URL
      const objectUrl = URL.createObjectURL(blob);
      
      // Check if we need to clear space
      if (this.currentSize + blob.size > this.maxSize) {
        this.clearOldEntries(blob.size);
      }
      
      // Add to cache
      this.cache.set(url, {
        objectUrl,
        size: blob.size,
        lastAccessed: Date.now()
      });
      
      this.currentSize += blob.size;
      
      return objectUrl;
    } catch (error) {
      console.error('Error setting item in media cache:', error);
      return null;
    }
  },
  
  clearOldEntries(requiredSpace) {
    // Sort entries by last accessed time (oldest first)
    const entries = Array.from(this.cache.entries())
      .sort((a, b) => a[1].lastAccessed - b[1].lastAccessed);
    
    // Remove entries until we have enough space
    let freedSpace = 0;
    
    for (const [url, entry] of entries) {
      // Revoke the object URL to free memory
      URL.revokeObjectURL(entry.objectUrl);
      
      // Remove from cache
      this.cache.delete(url);
      
      // Update size
      freedSpace += entry.size;
      this.currentSize -= entry.size;
      
      // Check if we've freed enough space
      if (this.currentSize + requiredSpace <= this.maxSize) {
        break;
      }
    }
  },
  
  clear() {
    // Revoke all object URLs
    for (const entry of this.cache.values()) {
      URL.revokeObjectURL(entry.objectUrl);
    }
    
    // Clear the cache
    this.cache.clear();
    this.currentSize = 0;
  }
};

// Media fetching hook
const useCachedMedia = (url, type = 'image') => {
  const [cachedUrl, setCachedUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cacheStatus, setCacheStatus] = useState('uncached');
  
  useEffect(() => {
    let isMounted = true;
    
    const loadMedia = async () => {
      if (!url) {
        setLoading(false);
        return;
      }
      
      try {
        // Try to get from cache first
        const cachedItem = await mediaCache.getItem(url);
        
        if (cachedItem && isMounted) {
          setCachedUrl(cachedItem);
          setCacheStatus('cached');
          setLoading(false);
          return;
        }
        
        // Not in cache, fetch it
        const response = await fetch(url, {
          headers: {
            // Add cache-control to prefer cached version
            'Cache-Control': 'no-cache'
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch media: ${response.status}`);
        }
        
        const blob = await response.blob();
        
        // Cache the blob
        const objectUrl = await mediaCache.setItem(url, blob);
        
        if (isMounted) {
          setCachedUrl(objectUrl || url);
          setCacheStatus(objectUrl ? 'newlyCached' : 'uncached');
          setLoading(false);
        }
      } catch (error) {
        console.error('Error loading media:', error);
        if (isMounted) {
          setError(error);
          setCachedUrl(url); // Fall back to original URL
          setCacheStatus('error');
          setLoading(false);
        }
      }
    };
    
    setLoading(true);
    loadMedia();
    
    return () => {
      isMounted = false;
    };
  }, [url]);
  
  return { cachedUrl, loading, error, cacheStatus };
};

const PostModal = ({ 
  isOwner,
  onEdit,
  onDelete,
  onReport,
  onShare,
  isMobile
}) => {
  const { modalState, closePostModal, changePost } = usePostContext();
  const { isOpen, posts, currentIndex, returnPath } = modalState;
  const location = useLocation();
  const [stories, setStories] = useState([]);
  const [showCaption, setShowCaption] = useState(true);
  const [expandedCaptions, setExpandedCaptions] = useState({});
  const [showMenu, setShowMenu] = useState(false);
  const prevPostsKeyRef = useRef('');
  const menuRef = useRef(null);
  
  const { getUserDataFromCache } = useContext(UserContext);
  const navigate = useNavigate();

  // Memoize currentPost
  const currentPost = useMemo(() => {
    return posts[currentIndex] || null;
  }, [posts, currentIndex]);

  // Handle story errors
  const handleStoryError = (error) => {
    console.warn('Story error caught:', error);
    // Prevent the error from bubbling up
    return true; // Return true to indicate error was handled
  };
  
  // Custom renderer for embedded audio
  const EmbedRenderer = ({ story, action }) => {
    const post = story.postData;
    console.log("Rendering audio embed:", post); // Debug log
    
    useEffect(() => {
      // Force iframe reload if needed
      if (post.embedHtml && post.type === 'audio') {
        const container = document.querySelector(`.${styles.embedPlayer}`);
        if (container) {
          // Reset HTML to force reload
          container.innerHTML = post.embedHtml;
        }
      }
    }, [post.embedHtml, post.type]);
    
    return (
      <div className={styles.embedContainer}>
        {post.embedHtml ? (
          <div 
            className={styles.embedPlayer}
            dangerouslySetInnerHTML={{ __html: post.embedHtml }}
          />
        ) : post.type === 'audio' ? (
          <div className={styles.audioFallback}>
            <FontAwesomeIcon icon={faMusic} size="3x" />
            <p>Audio player could not be loaded</p>
          </div>
        ) : null}
        
        {/* Caption overlay */}
        <CaptionOverlay 
          post={post}
          showCaption={showCaption}
          expandedCaptions={expandedCaptions}
          setExpandedCaptions={setExpandedCaptions}
          authorData={authorDataCache.get(post.userId)}
          onShare={() => handleShare(post)}
          onEdit={() => handleEdit(post)}
          onDelete={() => handleDelete(post.postId)}
          onReport={() => handleReport(post)}
          isOwner={isOwner}
          isMobile={isMobile}
          showMenu={showMenu}
          setShowMenu={setShowMenu}
        />
      </div>
    );
  };

  // Caption overlay component
  const CaptionOverlay = ({ 
    post, 
    showCaption, 
    expandedCaptions, 
    setExpandedCaptions,
    authorData,
    onShare,
    onEdit,
    onDelete,
    onReport,
    isOwner,
    isMobile,
    showMenu,
    setShowMenu,
    cacheStatus
  }) => {
    const isExpanded = expandedCaptions[post.postId] || false;
    
    if (!showCaption) return null;
    
    return (
      <div className={styles.captionOverlay}>
        {/* Header */}
        <div className={styles.captionHeader}>
          <div className={styles.userInfo}>
            <img 
              src={authorData?.profilePictureUrl || defaultProfilePhoto}
              alt={post.userName}
              className={styles.profilePhoto}
            />
            <div>
              <h4>{authorData?.fullName || post.userName}</h4>
              <span className={styles.postDate}>
                {new Date(post.createdAt.seconds * 1000).toLocaleDateString()}
                {cacheStatus === 'cached' && (
                  <span className={styles.cachedBadge}> • Cached</span>
                )}
              </span>
            </div>
          </div>
          
          <div className={styles.headerActions}>
            {!isMobile && post.link && (
              <button 
                className={styles.linkButton}
                onClick={() => window.open(post.link, '_blank')}
              >
                <FontAwesomeIcon icon={faLink} />
              </button>
            )}
            
            <button 
              className={styles.menuButton}
              onClick={(e) => {
                e.stopPropagation(); // Stop event from bubbling up
                e.preventDefault(); // Prevent default behavior
                setShowMenu(!showMenu);
                console.log("Menu toggled:", !showMenu); // Debug logging
              }}
            >
              <FontAwesomeIcon icon={faEllipsisV} />
            </button>
  
            {showMenu && (
              <div className={styles.dropdownMenu} ref={menuRef}>
                {isOwner ? (
                  <>
                    <button onClick={onEdit}>
                      <FontAwesomeIcon icon={faEdit} />
                      Edit
                    </button>
                    <button onClick={onDelete}>
                      <FontAwesomeIcon icon={faTrash} />
                      Delete
                    </button>
                  </>
                ) : (
                  <button onClick={onReport}>
                    <FontAwesomeIcon icon={faFlag} />
                    Report
                  </button>
                )}
                <button onClick={onShare}>
                  <FontAwesomeIcon icon={faShare} />
                  Share
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Caption */}
        {post.caption && (
          <div className={styles.captionContent}>
            <p 
              className={`${styles.captionText} ${isExpanded ? styles.expanded : ''}`}
              onClick={() => setExpandedCaptions(prev => ({
                ...prev,
                [post.postId]: !isExpanded
              }))}
            >
              {post.caption}
            </p>
            
            {post.tags && post.tags.length > 0 && (
              <div className={styles.tags}>
                {post.tags.map((tag, index) => (
                  <span key={index} className={styles.tag}>#{tag}</span>
                ))}
              </div>
            )}
            
            {isMobile && post.link && (
              <button 
                className={styles.viewLinkButton}
                onClick={() => window.open(post.link, '_blank')}
              >
                <FontAwesomeIcon icon={faLink} />
                View Link
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Custom renderer for carousel posts with caching
  const CachedCarouselRenderer = ({ story, action }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const post = story.postData;
    const authorData = authorDataCache.get(post.userId);
    
    // Use cached URL for the current image
    const { cachedUrl, loading, cacheStatus } = useCachedMedia(
      post.carousel?.[currentImageIndex]?.url || post.carousel?.[0]?.url,
      'image'
    );
    
    // Preload next images in the carousel
    useEffect(() => {
      if (!post.carousel || post.carousel.length <= 1) return;
      
      // Preload next 2 images
      const preloadNextImages = async () => {
        for (let i = 1; i <= 2; i++) {
          const nextIndex = (currentImageIndex + i) % post.carousel.length;
          if (post.carousel[nextIndex]?.url) {
            try {
              // Try to get from cache or fetch
              const cached = await mediaCache.getItem(post.carousel[nextIndex].url);
              if (!cached) {
                // Not in cache, fetch it
                const response = await fetch(post.carousel[nextIndex].url);
                if (response.ok) {
                  const blob = await response.blob();
                  await mediaCache.setItem(post.carousel[nextIndex].url, blob);
                }
              }
            } catch (error) {
              console.warn('Error preloading carousel image:', error);
            }
          }
        }
      };
      
      preloadNextImages();
    }, [currentImageIndex, post.carousel]);

    const handlePrevImage = (e) => {
      e.stopPropagation();
      if (currentImageIndex > 0) {
        setCurrentImageIndex(currentImageIndex - 1);
      }
    };
    
    const handleNextImage = (e) => {
      e.stopPropagation();
      if (currentImageIndex < post.carousel.length - 1) {
        setCurrentImageIndex(currentImageIndex + 1);
      }
    };
    
    return (
      <div className={styles.carouselContainer}>
        {loading ? (
          <div className={styles.loadingContainer}>
            <FontAwesomeIcon icon={faSpinner} spin size="3x" className={styles.spinner} />
          </div>
        ) : (
          <img 
            src={cachedUrl || post.carousel[currentImageIndex].url}
            alt={`Slide ${currentImageIndex + 1}`}
            className={styles.carouselImage}
          />
        )}
        
        {/* Carousel navigation */}
        {post.carousel.length > 1 && (
          <>
            {currentImageIndex > 0 && (
              <button 
                className={styles.carouselNavButton + ' ' + styles.carouselPrev}
                onClick={handlePrevImage}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
            )}
            
            {currentImageIndex < post.carousel.length - 1 && (
              <button 
                className={styles.carouselNavButton + ' ' + styles.carouselNext}
                onClick={handleNextImage}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            )}
            
            {/* Carousel indicators */}
            <div className={styles.carouselIndicators}>
              {post.carousel.map((_, index) => (
                <div 
                  key={index}
                  className={`${styles.indicator} ${index === currentImageIndex ? styles.active : ''}`}
                />
              ))}
            </div>
          </>
        )}
        
        {/* Caption overlay */}
        <CaptionOverlay 
          post={post}
          showCaption={true}
          expandedCaptions={expandedCaptions}
          setExpandedCaptions={setExpandedCaptions}
          authorData={authorData}
          onShare={() => handleShare(post)}
          onEdit={() => handleEdit(post)}
          onDelete={() => handleDelete(post.postId)}
          onReport={() => handleReport(post)}
          isOwner={isOwner}
          isMobile={isMobile}
          showMenu={showMenu}
          setShowMenu={setShowMenu}
          cacheStatus={cacheStatus}
        />
      </div>
    );
  };

  // Custom renderer for uploaded audio with caching
  const CachedAudioRenderer = ({ story, action }) => {
    const post = story.postData;
    const { cachedUrl: cachedAudioUrl, loading: audioLoading, cacheStatus: audioCacheStatus } = useCachedMedia(post.url, 'audio');
    const { cachedUrl: cachedCoverUrl, loading: coverLoading } = useCachedMedia(post.thumbnailUrl || require('../../assets/Images/defaultUserProfilePicture.png'), 'image');
    
    return (
      <div className={styles.audioContainer}>
        {/* Cover image */}
        <div className={styles.audioCoverContainer}>
          {coverLoading ? (
            <div className={styles.loadingContainer}>
              <FontAwesomeIcon icon={faSpinner} spin size="3x" className={styles.spinner} />
            </div>
          ) : (
            <img 
              src={cachedCoverUrl || post.thumbnailUrl || require('../../assets/Images/defaultUserProfilePicture.png')}
              alt={post.caption || 'Audio cover'}
              className={styles.audioCover}
            />
          )}
          <div className={styles.audioOverlay}></div>
        </div>
        
        {/* Audio player */}
        <div className={styles.audioPlayerWrapper}>
          {audioLoading ? (
            <div className={styles.loadingIndicator}>
              <FontAwesomeIcon icon={faSpinner} spin />
              <span>Loading audio...</span>
            </div>
          ) : (
            <audio 
              src={cachedAudioUrl || post.url}
              controls
              className={styles.audioPlayer}
              autoPlay={false}
            />
          )}
          
          {audioCacheStatus === 'cached' && (
            <div className={styles.cacheStatus}>
              <span>Playing from cache</span>
            </div>
          )}
        </div>
        
        {/* Caption overlay */}
        <CaptionOverlay 
          post={post}
          showCaption={true}
          expandedCaptions={expandedCaptions}
          setExpandedCaptions={setExpandedCaptions}
          authorData={authorDataCache.get(post.userId)}
          onShare={() => handleShare(post)}
          onEdit={() => handleEdit(post)}
          onDelete={() => handleDelete(post.postId)}
          onReport={() => handleReport(post)}
          isOwner={isOwner}
          isMobile={isMobile}
          showMenu={showMenu}
          setShowMenu={setShowMenu}
          cacheStatus={audioCacheStatus}
        />
      </div>
    );
  };
  
  // Custom renderer for video posts with caching
  const CachedVideoRenderer = ({ story, action, isPaused }) => {
    const post = story.postData;
    const authorData = authorDataCache.get(post.userId);
    const videoRef = useRef(null);
    const { cachedUrl, loading, cacheStatus } = useCachedMedia(post.url, 'video');
    
    // Pause/play based on isPaused prop
    useEffect(() => {
      if (videoRef.current) {
        if (isPaused && !videoRef.current.paused) {
          videoRef.current.pause();
        } else if (!isPaused && videoRef.current.paused) {
          videoRef.current.play().catch(e => console.log('Autoplay prevented:', e));
        }
      }
    }, [isPaused]);
    
    return (
      <div className={styles.videoContainerWrapper}>
        <div 
          className={styles.videoContainer} 
          onClick={(e) => e.stopPropagation()} // Prevent Stories default click behavior
        >
          {loading ? (
            <div className={styles.loadingContainer}>
              <FontAwesomeIcon icon={faSpinner} spin size="3x" className={styles.spinner} />
            </div>
          ) : (
            <video 
              ref={videoRef}
              src={cachedUrl || post.url}
              className={styles.videoPlayer}
              controls
              autoPlay={!isPaused}
              playsInline
              poster={post.thumbnailUrl}
            />
          )}
          
          {/* Custom caption overlay */}
          <div className={styles.customCaptionOverlay}>
            <div className={styles.captionHeader}>
              <div className={styles.userInfo}>
                <img 
                  src={authorData?.profilePictureUrl || defaultProfilePhoto}
                  alt={post.userName}
                  className={styles.profilePhoto}
                />
                <div>
                  <h4>{authorData?.fullName || post.userName}</h4>
                  <span className={styles.postDate}>
                    {new Date(post.createdAt.seconds * 1000).toLocaleDateString()}
                    {cacheStatus === 'cached' && (
                      <span className={styles.cachedBadge}> • Cached</span>
                    )}
                  </span>
                </div>
              </div>
              
              <div className={styles.headerActions}>
                {!isMobile && post.link && (
                  <button 
                    className={styles.linkButton}
                    onClick={() => window.open(post.link, '_blank')}
                  >
                    <FontAwesomeIcon icon={faLink} />
                  </button>
                )}
                
                <button 
                  className={styles.menuButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setShowMenu(!showMenu);
                  }}
                >
                  <FontAwesomeIcon icon={faEllipsisV} />
                </button>
                
                {showMenu && (
                  <div className={styles.dropdownMenu} ref={menuRef}>
                    {isOwner ? (
                      <>
                        <button onClick={() => handleEdit(post)}>
                          <FontAwesomeIcon icon={faEdit} />
                          Edit
                        </button>
                        <button onClick={() => handleDelete(post.postId)}>
                          <FontAwesomeIcon icon={faTrash} />
                          Delete
                        </button>
                      </>
                    ) : (
                      <button onClick={() => handleReport(post)}>
                        <FontAwesomeIcon icon={faFlag} />
                        Report
                      </button>
                    )}
                    <button onClick={() => handleShare(post)}>
                      <FontAwesomeIcon icon={faShare} />
                      Share
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Caption */}
            {post.caption && (
              <div className={styles.captionContent}>
                <p 
                  className={`${styles.captionText} ${expandedCaptions[post.postId] ? styles.expanded : ''}`}
                  onClick={() => setExpandedCaptions(prev => ({
                    ...prev,
                    [post.postId]: !expandedCaptions[post.postId]
                  }))}
                >
                  {post.caption}
                </p>
                
                {post.tags && post.tags.length > 0 && (
                  <div className={styles.tags}>
                    {post.tags.map((tag, index) => (
                      <span key={index} className={styles.tag}>#{tag}</span>
                    ))}
                  </div>
                )}
                
                {isMobile && post.link && (
                  <button 
                    className={styles.viewLinkButton}
                    onClick={() => window.open(post.link, '_blank')}
                  >
                    <FontAwesomeIcon icon={faLink} />
                    View Link
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  // Custom renderer for image posts with caching
  const CachedImageRenderer = ({ story, action }) => {
    const post = story.postData;
    const authorData = authorDataCache.get(post.userId);
    const { cachedUrl, loading, cacheStatus } = useCachedMedia(post.url, 'image');
    
    return (
      <div className={styles.imageContainer}>
        {loading ? (
          <div className={styles.loadingContainer}>
            <FontAwesomeIcon icon={faSpinner} spin size="3x" className={styles.spinner} />
          </div>
        ) : (
          <img 
            src={cachedUrl || post.url}
            alt={post.caption || 'Post'}
            className={styles.postImage}
          />
        )}
        
        <CaptionOverlay 
          post={post}
          showCaption={showCaption}
          expandedCaptions={expandedCaptions}
          setExpandedCaptions={setExpandedCaptions}
          authorData={authorData}
          onShare={() => handleShare(post)}
          onEdit={() => handleEdit(post)}
          onDelete={() => handleDelete(post.postId)}
          onReport={() => handleReport(post)}
          isOwner={isOwner}
          isMobile={isMobile}
          showMenu={showMenu}
          setShowMenu={setShowMenu}
          cacheStatus={cacheStatus}
        />
      </div>
    );
  };
  
  // Convert posts to stories format with cached renderers
  useEffect(() => {
    if (!posts || posts.length === 0 || !isOpen) {
      setStories([]);
      return;
    }
    
    const postsKey = posts.map(p => `${p.postId}-${p.updatedAt?.seconds || ''}`).join('|');
    
    if (postsKey === prevPostsKeyRef.current) return;
    
    const convertPostsToStories = async () => {
      // Fetch all author data in parallel
      const authorPromises = posts.map(async (post) => {
        if (!authorDataCache.has(post.userId)) {
          try {
            const userData = await getUserDataFromCache(post.userId);
            authorDataCache.set(post.userId, userData);
          } catch (error) {
            console.error('Error fetching author data:', error);
          }
        }
      });
      
      await Promise.all(authorPromises);
      
      const storiesData = posts.map(post => {
        const authorData = authorDataCache.get(post.userId);
        
        // Common story properties
        const commonProps = {
          duration: 999999999, // Essentially infinite to prevent auto-advance
          header: {
            heading: post.userName,
            subheading: new Date(post.createdAt.seconds * 1000).toLocaleDateString(),
            profileImage: authorData?.profilePictureUrl || defaultProfilePhoto
          },
          postData: post // Pass the full post data
        };
        
        // Handle carousel posts
        if (post.isCarousel && post.carousel?.length > 0) {
          return {
            ...commonProps,
            content: CachedCarouselRenderer
          };
        }
        
        // Handle embedded audio
        if (post.isEmbed && post.embedHtml) {
          return {
            ...commonProps,
            content: EmbedRenderer
          };
        }
        
        // Handle video posts
        if (post.type === 'video') {
          return {
            ...commonProps,
            url: post.url,
            type: 'video',
            content: CachedVideoRenderer
          };
        }
        
        // Handle audio posts (non-embedded)
        if (post.type === 'audio' && !post.isEmbed) {
          return {
            ...commonProps,
            content: CachedAudioRenderer
          };
        }
        
        // Handle image posts
        return {
          ...commonProps,
          url: post.url,
          content: CachedImageRenderer
        };
      });
      
      setStories(storiesData);
      prevPostsKeyRef.current = postsKey;
    };
    
    convertPostsToStories();
  }, [posts, isOpen, showCaption, expandedCaptions, isOwner, isMobile, getUserDataFromCache, showMenu]);

  // Clean up effect when component unmounts
  useEffect(() => {
    return () => {
      // Clean up event listeners
      const storyElements = document.querySelectorAll('video, audio, iframe');
      storyElements.forEach(element => {
        element.pause && element.pause();
        element.src = '';
        element.load && element.load();
      });
      
      // Don't clear the cache on unmount to keep it for future use
    };
  }, []);
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    
    if (showMenu) {
      // Use capture phase to ensure we get the event first
      document.addEventListener('mousedown', handleClickOutside, true);
      document.addEventListener('touchstart', handleClickOutside, true);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside, true);
        document.removeEventListener('touchstart', handleClickOutside, true);
      };
    }
  }, [showMenu]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      prevPostsKeyRef.current = '';
      setExpandedCaptions({});
      setShowMenu(false);
    }
  }, [isOpen]);
  
  // Handlers
  const handleClose = () => {
    if (returnPath && location.pathname !== returnPath) {
      navigate(returnPath);
    }
    closePostModal();
  };
  
  const handleEdit = (post) => {
    handleClose();
    onEdit?.(post);
  };
  
  const handleDelete = (postId) => {
    handleClose();
    onDelete?.(postId);
  };
  
  const handleReport = (post) => {
    handleClose();
    onReport?.(post);
  };
  
  const handleShare = (post) => {
    onShare?.(post);
  };
  
  const handleAllStoriesEnd = () => {
    handleClose();
  };
  
  if (!isOpen || !stories.length) return null;
  
  const width = isMobile ? '100%' : '400px';
  const height = isMobile ? '100vh' : '80vh';
  
  return (
    <div className={styles.modalOverlay}>
      <div 
        className={styles.modalContent} 
        style={{ 
          width, 
          height, 
          maxWidth: isMobile ? '100%' : '400px',
          margin: '0 auto'
        }}
      >
        <button className={styles.closeButton} onClick={handleClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        
        {/* Caption toggle button */}
        <button 
          className={styles.captionToggle}
          onClick={() => setShowCaption(!showCaption)}
        >
          <FontAwesomeIcon icon={showCaption ? faEyeSlash : faEye} />
        </button>
        
        <Stories
          stories={stories}
          defaultInterval={999999999} // Prevent auto-advance
          width="100%"
          height="100%"
          currentIndex={currentIndex}
          onAllStoriesEnd={handleAllStoriesEnd}
          keyboardNavigation
          preventDefault={true} // Set to true to prevent default touch behavior
          loop={false}
          onError={handleStoryError} // Add error handler
          preloadCount={5} // Preload more stories to reduce loading issues
        />
      </div>
    </div>
  );
};

export default PostModal;